import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, Mail } from "lucide-react";
import Logo from "./Logo";
import { SITE, NAV_LINKS } from "@/data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 bg-white transition-shadow ${
        scrolled ? "shadow-[0_2px_12px_rgba(10,37,64,0.08)]" : ""
      }`}
      data-testid="navbar"
    >
      {/* Utility bar */}
      <div className="hidden bg-navy-950 text-white md:block" data-testid="nav-utility-bar">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2 text-xs">
          <p className="tracking-wide text-navy-100">
            Professional Consulting · Finance · Tax · Compliance · Advisory
          </p>
          <div className="flex items-center gap-5">
            <a href={`tel:${SITE.phoneTel}`} className="inline-flex items-center gap-1.5 hover:text-gold-soft" data-testid="nav-utility-phone">
              <Phone className="h-3 w-3" /> {SITE.phoneDisplay}
            </a>
            <a href={`mailto:${SITE.email}`} className="inline-flex items-center gap-1.5 hover:text-gold-soft" data-testid="nav-utility-email">
              <Mail className="h-3 w-3" /> {SITE.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="border-b border-line">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-6">
          <Link to="/" aria-label="SAIVU home" data-testid="nav-logo-link">
            <Logo compact />
          </Link>

          <nav className="hidden items-center gap-8 md:flex" data-testid="nav-links">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `border-b-2 pb-1 text-sm transition-colors ${
                    isActive
                      ? "border-gold font-semibold text-navy-900"
                      : "border-transparent text-ink hover:text-navy-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-4 md:flex">
            <Link
              to="/contact"
              className="rounded-md bg-navy-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-navy-800"
              data-testid="nav-cta-button"
            >
              Book free consult
            </Link>
          </div>

          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-line text-navy-900 md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile panel */}
        {open && (
          <div className="border-t border-line bg-white px-5 pb-6 pt-2 md:hidden" data-testid="nav-mobile-menu">
            <nav className="flex flex-col">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}-link`}
                  className={({ isActive }) =>
                    `border-b border-line py-3.5 text-base ${
                      isActive ? "font-semibold text-navy-900" : "text-ink"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
            <Link
              to="/contact"
              className="mt-5 inline-flex w-full items-center justify-center rounded-md bg-navy-900 px-6 py-3 text-sm font-semibold text-white"
              data-testid="nav-mobile-cta-button"
            >
              Book free consult
            </Link>
            <a
              href={`tel:${SITE.phoneTel}`}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-line px-6 py-3 text-sm text-navy-900"
              data-testid="nav-mobile-phone-link"
            >
              <Phone className="h-4 w-4 text-gold-deep" />
              {SITE.phoneDisplay}
            </a>
          </div>
        )}
      </div>
    </header>
  );
}
